
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Define a custom type for elements with _intersectionCallback
interface ElementWithCallback extends HTMLElement {
  _intersectionCallback?: (isIntersecting: boolean, entry: IntersectionObserverEntry) => void;
}

@Injectable({
  providedIn: 'root'
})
export class IntersectionObserverService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private observer!: IntersectionObserver;

  constructor(private ngZone: NgZone) {
    this.createObserver();
  }

  private createObserver(): void {
    this.observer = new IntersectionObserver((entries) => {
      this.ngZone.run(() => {
        entries.forEach(entry => {
          const target = entry.target as ElementWithCallback;
          const callback = target['_intersectionCallback'];
          if (callback && typeof callback === 'function') {
            callback(entry.isIntersecting, entry);
          }
        });
      });
    }, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    });
  }

  observe(element: ElementWithCallback): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      const callback = (isIntersecting: boolean) => {
        subscriber.next(isIntersecting);
        if (isIntersecting) {
          this.unobserve(element);
          subscriber.complete();
        }
      };

      // Store callback on element
      element['_intersectionCallback'] = callback;

      this.observer.observe(element);

      return () => {
        this.unobserve(element);
        delete element['_intersectionCallback'];
      };
    }).pipe(takeUntil(this.destroy$));
  }

  unobserve(element: ElementWithCallback): void {
    if (this.observer) {
      this.observer.unobserve(element);
      delete element['_intersectionCallback'];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}